import React, { useState } from 'react';
import { LoanNumber } from 'Feature/Arrears/components/ArrearsInquiryPage/core/LoanNumber';
import { Ordering } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Ordering';
import { Paging } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Paging';
import { useArrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/useArrears';
import { ArrearsUserProvider } from 'Feature/Arrears/components/ArrearsInquiryPage/core/users/ArrearsUserProvider';
import {
  BreadcrumbComponent,
  BreadcrumbComponentItem
} from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/BreadcrumbComponent';
import Header from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/Header';
import MainCard from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/MainCard';
import SpinnerSection from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/SpinnerSection';
import Title from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/Title';
import SearchByLoanNumberInput from 'Feature/Arrears/components/ArrearsInquiryPage/ui/SearchByLoanNumberInput';
import SearchResultSection from 'Feature/Arrears/components/ArrearsInquiryPage/ui/SearchResultSection';
import { useTranslation } from 'react-i18next';
import { ToastNotification, ToastNotificationVariant } from '@cmhc-schl/hearth-ui';
import { Arrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Arrears';

interface ToastNotificationState {
  open: boolean;
  header: string;
  body: string;
}
const ArrearsInquiryPage = () => {
  const { t } = useTranslation('HbtArrearsInquiryPage');
  const defaultSuccessNotification = { open: false, header: '', body: '' };
  const [loanNumber, setLoanNumber] = useState<LoanNumber>(LoanNumber.empty());
  const {
    arrears,
    latestArrears,
    loadingArrears,
    fetchArrearsError,
    clearArrears,
    fetchArrears,
    reloadingArrears,
    changePaging,
    changeOrdering,
    updateArrears
  } = useArrears();
  const [successNotification, setSuccessNotification] = useState<ToastNotificationState>(
    defaultSuccessNotification
  );

  const breadcrumbItems: BreadcrumbComponentItem[] = [
    { label: t('breadcrumb.dashboard.label'), href: t('breadcrumb.dashboard.href') },
    { label: t('breadcrumb.current.label') }
  ];

  const newLoanNumber = async (loanNumber: LoanNumber) => {
    if (loanNumber.status === 'empty') {
      clearSearchResults();
    }
    if (loanNumber.status === 'valid') {
      setLoanNumber(loanNumber);
      await fetchArrears(loanNumber);
    }
    if (loanNumber.status === 'invalid') {
      setLoanNumber(loanNumber);
      clearArrears();
    }
  };

  const clearSearchResults = () => {
    setLoanNumber(LoanNumber.empty());
    clearArrears();
  };

  const changeArrearsPaging = async (paging: Paging) => {
    if (loanNumber.status === 'valid') {
      await changePaging(loanNumber, paging);
    }
  };

  const changeArrearsOrdering = async (ordering: Ordering) => {
    if (loanNumber.status === 'valid') {
      await changeOrdering(loanNumber, ordering);
    }
  };

  const onArrearsUpdated = (updatedArrears: Arrears) => {
    setSuccessNotification({
      open: true,
      header: t('toastSection.update.success.header'),
      body: t('toastSection.update.success.message')
    });
    updateArrears(updatedArrears);
  };

  const onArrearsDeleted = async () => {
    setSuccessNotification({
      open: true,
      header: t('toastSection.delete.success.header'),
      body: t('toastSection.delete.success.message')
    });
    await newLoanNumber(loanNumber);
  };

  return (
    <ArrearsUserProvider>
      <Title title={t('title')} />
      <BreadcrumbComponent items={breadcrumbItems} />
      <MainCard>
        <Header size="large">{t('title')}</Header>
        <SearchByLoanNumberInput loanNumber={loanNumber} setLoanNumber={newLoanNumber} />
        <SpinnerSection loading={loadingArrears}>
          <SearchResultSection
            loanNumber={loanNumber}
            arrears={arrears}
            latestArrears={latestArrears}
            reloadingArrears={reloadingArrears}
            error={fetchArrearsError}
            clearSearchResults={clearSearchResults}
            changePaging={changeArrearsPaging}
            changeOrdering={changeArrearsOrdering}
            onArrearsUpdated={onArrearsUpdated}
            onArrearsDeleted={onArrearsDeleted}
          />
        </SpinnerSection>
        <ToastNotification
          open={successNotification.open}
          header={{ value: successNotification.header }}
          subHeader={{ value: successNotification.body }}
          variant={ToastNotificationVariant.INFO}
          icon="icon_info_outlined"
          onClose={() => {
            setSuccessNotification(defaultSuccessNotification);
          }}
          dismissMode={'auto'}
        />
      </MainCard>
    </ArrearsUserProvider>
  );
};

export default ArrearsInquiryPage;
